import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { evalVisibility } from "@/components/FormBuilder";
import { SignaturePad } from "@/components/SignaturePad";
import { PerProductField, ProductRepeaterField, ActionsApplied } from "@/components/FormProductFields";
import { FormMediaField, YesNoNa } from "@/components/FormMediaField";
import { Loader2, X, ChevronDown, Send, Printer, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { TrailerSelect, ForkliftSelect } from "@/components/TrailerSelect";

const selCls = "w-full appearance-none rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 pr-8 text-sm text-slate-900 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 focus:outline-none";
const Chevron = () => <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />;

const fmtDT = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
const idNum = (id) => { let h = 0; for (const c of (id || "")) h = (h * 31 + c.charCodeAt(0)) % 90000; return 10000 + h; };

// Fill a form (formId) or view a submission (submission). In view mode an "Edit"
// button flips back to fill mode using the submission's form + answers.
const _TRUE = new Set(["true", "yes", "on", "1", "y", "checked"]);
const _FALSE = new Set(["false", "no", "off", "0", "n", "unchecked"]);
// Interpret a toggle/boolean answer across every shape different clients store it in:
// web saves "Yes"/"No"; mobile + offline sync save boolean true/false (also seen: 1/0,
// "true"/"false"). Returns "Yes" | "No" | null (not set).
function toggleState(v) {
  if (v === true) return "Yes";
  if (v === false) return "No";
  if (typeof v === "number") return v ? "Yes" : "No";
  if (typeof v === "string") {
    const k = v.trim().toLowerCase();
    if (_TRUE.has(k)) return "Yes";
    if (_FALSE.has(k)) return "No";
  }
  return null;
}
// Normalise a stored date answer to the YYYY-MM-DD an <input type="date"> needs, tolerating
// ISO datetimes and dd/mm/yyyy (common when typed on mobile). Returns "" if unparseable.
function asDateInput(v) {
  if (v === null || v === undefined || v === "") return "";
  const s = String(v).trim();
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = s.match(/^(\d{1,2})[/.\-](\d{1,2})[/.\-](\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  const d = new Date(s);
  return isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
}

// Resolve a file/image/video answer across client shapes: web stores {attachment_id, filename};
// mobile/offline may store just {attachment_id} (no filename), a bare id string, or a url/uri.
// The submission's attachment_refs (field_id -> {attachment_id, filename, mime}) is the most
// reliable source and is built identically by the web and engineer-app submit paths.
function resolveFileRef(fieldId, val, attachmentRefs) {
  const byField = (attachmentRefs || []).find((r) => r.field_id === fieldId);
  if (byField?.attachment_id) return { id: byField.attachment_id, filename: byField.filename, mime: byField.mime };
  if (val && typeof val === "object") {
    const id = val.attachment_id || val.id || val.aid;
    if (id) return { id, filename: val.filename || val.name, mime: val.mime || val.content_type };
    if (val.url || val.uri) return { url: val.url || val.uri, filename: val.filename || val.name };
  }
  if (typeof val === "string" && val) {
    if (/^https?:\/\//i.test(val)) return { url: val };
    return { id: val };
  }
  return null;
}
function isImageRef(ref) {
  if ((ref.mime || "").toLowerCase().startsWith("image/")) return true;
  return /\.(png|jpe?g|gif|webp|heic|heif|bmp)(\?|$)/i.test(ref.filename || ref.url || "");
}
function FileAnswerView({ fieldId, val, attachmentRefs }) {
  const ref = resolveFileRef(fieldId, val, attachmentRefs);
  const [url, setUrl] = useState(ref?.url || null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let active = true;
    if (ref && ref.id && !ref.url) {
      api.get(`/attachments/${ref.id}/view`).then((r) => active && setUrl(r.data.url)).catch(() => active && setErr(true));
    }
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref?.id]);
  if (!ref) return <div className="text-sm text-slate-500" data-testid={`file-view-${fieldId}`}>No file</div>;
  const label = ref.filename || "Attachment";
  if (err) return <div className="text-sm text-amber-600" data-testid={`file-view-${fieldId}`}>📎 {label} — not available yet (scanning or blocked)</div>;
  if (!url) return <div className="text-sm text-slate-400" data-testid={`file-view-${fieldId}`}>Loading attachment…</div>;
  return isImageRef(ref)
    ? <a href={url} target="_blank" rel="noreferrer" data-testid={`file-view-${fieldId}`} className="inline-block"><img src={url} alt={label} className="max-h-48 rounded-lg border border-slate-200 object-contain" /></a>
    : <a href={url} target="_blank" rel="noreferrer" data-testid={`file-view-${fieldId}`} className="text-sm text-indigo-600 hover:underline">📎 {label}</a>;
}

export default function FormFiller({ formId, submission, context = {}, mapTaskId, preview, canFill, onClose, onSubmitted, locked, submitUrl, formUrl }) {
  const [editing, setEditing] = useState(false);
  const viewing = !!submission && !editing;
  const previewing = !!preview && !submission && !editing;
  const effectiveFormId = formId || submission?.form_id;
  const [form, setForm] = useState(submission ? { name: submission.form_name, fields: submission.fields } : null);
  const [answers, setAnswers] = useState(submission?.answers || {});
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (!submission && formId) api.get(formUrl || `/forms/${formId}`).then((r) => {
      setForm(r.data);
      const today = new Date().toISOString().slice(0, 10);
      const pre = {};
      (r.data.fields || []).forEach((f) => {
        if (f.default === "today") { pre[f.id] = today; }
        if (f.default === "current_user") { pre[f.id] = user?.name || user?.email || ""; }
        if (f.default === "now") { pre[f.id] = new Date().toTimeString().slice(0, 5); }
      });
      setAnswers((s) => ({ ...pre, ...s }));
    }).catch(() => toast.error("Could not load form"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, submission]);

  const set = (id, v) => setAnswers((s) => ({ ...s, [id]: v }));
  const subIdRef = useRef((window.crypto?.randomUUID && window.crypto.randomUUID()) || `sub-${Date.now()}-${Math.random().toString(16).slice(2)}`);

  // Form-captured files upload via the real scanned pipeline, against the JOB, so they
  // land in the central Job Files panel. The answer stores the attachment reference.
  const uploadFile = async (fid, file, multiple) => {
    if (!file) return;
    const trailer = form?.name === "Trailer Roadworthiness Inspection" && !context.task_legacy_id;
    const forklift = form?.name === "Forklift check sheet" && !context.task_legacy_id;
    const recType = trailer ? "trailer_inspections" : forklift ? "forklift_checks" : "tasks";
    const recId = trailer || forklift ? subIdRef.current : context.task_legacy_id;
    if (!recId) { toast.error("Files can only be attached when filling a form on a job"); return; }
    if (multiple) {
      const tmp = `up-${Date.now()}-${file.name}`;
      const preview = URL.createObjectURL(file);
      setAnswers((s) => ({ ...s, [fid]: [...(Array.isArray(s[fid]) ? s[fid] : []), { uploading: true, filename: file.name, mime: file.type, tmp, preview }] }));
      const swap = (v) => setAnswers((s) => ({ ...s, [fid]: (s[fid] || []).flatMap((x) => (x.tmp === tmp ? (v ? [{ ...v, preview }] : []) : [x])) }));
      try {
        const fd = new FormData();
        fd.append("record_type", recType); fd.append("record_id", recId); fd.append("category", "Form Upload");
        fd.append("client_key", `${effectiveFormId}:${fid}:${file.name}:${file.size}`); fd.append("file", file);
        const r = await api.post("/attachments/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        swap({ attachment_id: r.data.attachment.id, filename: file.name, mime: file.type });
        toast.success(`${file.name} uploaded — scanning for threats`);
      } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); swap(null); }
      return;
    }
    set(fid, { uploading: true, filename: file.name });
    try {
      const fd = new FormData();
      fd.append("record_type", recType);
      fd.append("record_id", recId);
      fd.append("category", "Form Upload");
      fd.append("client_key", `${effectiveFormId}:${fid}:${file.name}:${file.size}`);
      fd.append("file", file);
      const r = await api.post("/attachments/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set(fid, { attachment_id: r.data.attachment.id, filename: file.name, mime: file.type });
      toast.success("File uploaded — scanning for threats");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); set(fid, undefined); }
  };
  const toggleMulti = (id, opt) => setAnswers((s) => {
    const cur = Array.isArray(s[id]) ? s[id] : [];
    return { ...s, [id]: cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt] };
  });

  const fields = form?.fields || [];
  const visible = useMemo(() => fields.filter((f) => evalVisibility(f, answers)), [fields, answers]);

  const submit = async () => {
    const missing = visible.filter((f) => f.type !== "section" && f.required &&
      (answers[f.id] === undefined || answers[f.id] === "" || answers[f.id] === false ||
        (Array.isArray(answers[f.id]) && answers[f.id].length === 0) ||
        (f.type === "product_repeater" && !answers[f.id].some((r) => r.product_legacy_id))));
    if (visible.some((f) => Array.isArray(answers[f.id]) && answers[f.id].some((x) => x?.uploading))) { toast.error("Please wait for uploads to finish"); return; }
    if (missing.length) { toast.error(`Please complete: ${missing.map((m) => m.label || "a required field").slice(0, 3).join(", ")}`); return; }
    setBusy(true);
    const clean = Object.fromEntries(Object.entries(answers).map(([k, v]) => [k, Array.isArray(v) ? v.map((x) => (x && typeof x === "object" && x.preview ? (({ preview, ...rest }) => rest)(x) : x)) : v]));
    const send = (confirm) => api.post(submitUrl || `/forms/${effectiveFormId}/submissions`, { answers: clean, task_legacy_id: context.task_legacy_id || null, shift_id: context.shift_id || null, requirement_ref: context.requirement_ref || null, form_assignment_id: context.form_assignment_id || null, client_submission_id: subIdRef.current, confirm_duplicate: confirm });
    try {
      let r;
      try { r = await send(false); } catch (e) {
        const d = e.response?.data?.detail;
        if (e.response?.status !== 409 || d?.code !== "duplicate_payment_ref") throw e;
        const when = d.submitted_at ? new Date(d.submitted_at).toLocaleString("en-GB") : "";
        const amt = d.amount ? ` for £${Number(d.amount).toLocaleString("en-GB", { minimumFractionDigits: 2 })}` : "";
        if (!window.confirm(`Reference "${d.ref}" has already been used on this job${amt} (${d.form_name}, by ${d.submitted_by || "someone"}${when ? ` on ${when}` : ""}).\n\nSave this payment anyway?`)) return;
        r = await send(true);
      }
      const ticked = r.data?.requirements_ticked || [];
      toast.success(ticked.length ? `Form submitted · completed ${ticked.length} requirement${ticked.length > 1 ? "s" : ""}` : "Form submitted");
      onSubmitted?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    finally { setBusy(false); }
  };

  const ctxLabel = context.task_legacy_id ? `Job #${context.task_legacy_id}` : context.shift_id ? `Shift ${context.shift_id}` : null;
  const dis = viewing || previewing;

  const renderFields = () => (
    <>
      {form?.description && <p className="text-sm text-slate-500">{form.description}</p>}
      {visible.map((f) => {
        if (f.type === "section") return <h3 key={f.id} className="text-sm font-bold text-slate-800 uppercase tracking-wide border-b border-slate-200 pb-1 pt-2">{f.label || "Section"}</h3>;
        if (f.type === "notice") return <div key={f.id} className="rounded-xl border-2 border-amber-300 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900" data-testid={`fill-notice-${f.id}`}>{f.label}</div>;
        const val = answers[f.id];
        const lbl = <label className="block text-sm font-medium text-slate-700 mb-1">{f.label || "Untitled"} {f.required && <span className="text-rose-500">*</span>}</label>;
        if (f.per_product || f.type === "product_repeater") return (
          <div key={f.id} data-testid={`fill-field-${f.id}`}>
            {lbl}
            {f.per_product
              ? <PerProductField field={f} value={val} disabled={dis} taskId={context.task_legacy_id} onChange={(v) => set(f.id, v)} />
              : <ProductRepeaterField field={f} value={val} disabled={dis} onChange={(v) => set(f.id, v)} />}
            {f.help && <p className="text-xs text-slate-400 mt-1">{f.help}</p>}
          </div>
        );
        if (f.multiple && (f.type === "file" || f.type === "image" || f.type === "video") && f.accept) {
          const list = Array.isArray(val) ? val : val ? [val] : [];
          return (
            <div key={f.id} data-testid={`fill-field-${f.id}`}>
              {lbl}
              <FormMediaField field={f} list={list} disabled={dis} onAdd={(file) => uploadFile(f.id, file, true)} onRemove={(i) => set(f.id, list.filter((_, idx) => idx !== i))} />
              {f.help && <p className="text-xs text-slate-400 mt-1">{f.help}</p>}
            </div>
          );
        }
        if (f.multiple && (f.type === "file" || f.type === "image" || f.type === "video")) {
          const list = Array.isArray(val) ? val : val ? [val] : [];
          return (
            <div key={f.id} data-testid={`fill-field-${f.id}`}>
              {lbl}
              <div className="flex flex-wrap gap-2">
                {dis && list.map((x, i) => {
                  const ref = (submission?.attachment_refs || []).filter((r) => r.field_id === f.id && r.attachment_id === x?.attachment_id);
                  return <FileAnswerView key={i} fieldId={`${f.id}-${i}`} val={x} attachmentRefs={ref.map((r) => ({ ...r, field_id: `${f.id}-${i}` }))} />;
                })}
                {dis && !list.length && <div className="text-sm text-slate-500">No files</div>}
              </div>
              {!dis && <>
                <input type="file" multiple accept={f.accept === "media" ? "image/*,video/*" : undefined}
                  onChange={(e) => { Array.from(e.target.files || []).forEach((file) => uploadFile(f.id, file, true)); e.target.value = ""; }}
                  className="block w-full text-sm text-slate-500 file:mr-3 file:rounded-lg file:border-0 file:bg-indigo-50 file:px-3 file:py-1.5 file:text-indigo-700" data-testid={`file-input-${f.id}`} />
                <ul className="mt-1 space-y-0.5" data-testid={`file-list-${f.id}`}>
                  {list.map((x, i) => (
                    <li key={x.tmp || x.attachment_id || i} className={`flex items-center gap-2 text-xs ${x.uploading ? "text-slate-400" : "text-emerald-600"}`}>
                      {x.uploading ? `Uploading ${x.filename}…` : `Attached: ${x.filename}`}
                      {!x.uploading && <button type="button" className="text-slate-400 hover:text-rose-600" onClick={() => set(f.id, list.filter((_, idx) => idx !== i))} data-testid={`file-remove-${f.id}-${i}`}><X className="w-3 h-3" /></button>}
                    </li>
                  ))}
                </ul>
              </>}
              {f.help && <p className="text-xs text-slate-400 mt-1">{f.help}</p>}
            </div>
          );
        }
        return (
          <div key={f.id} data-testid={`fill-field-${f.id}`}>
            {f.type !== "checkbox" && lbl}
            {f.type === "trailer" && <TrailerSelect value={val} disabled={dis} onPick={(t) => setAnswers((s) => ({ ...s, [f.id]: t?.legacy_id || "", tr_id: t ? (t.registration || t.title) : "" }))} />}
            {f.type === "forklift" && <ForkliftSelect value={val} disabled={dis} onPick={(t) => setAnswers((s) => ({ ...s, [f.id]: t?.legacy_id || "", flt_id: t ? t.title : "", flt_model: s.flt_model || (t?.model || "") }))} />}
            {f.type === "time" && <Input type="time" value={val || ""} disabled={dis} onChange={(e) => set(f.id, e.target.value)} data-testid={`fill-time-${f.id}`} />}
            {f.type === "text" && <Input value={val || ""} disabled={dis || (f.id === "tr_id" && !!answers.tr_asset) || (f.id === "flt_id" && !!answers.flt_asset)} onChange={(e) => set(f.id, e.target.value)} placeholder={f.placeholder} data-testid={f.id === "tr_id" ? "trailer-reg-input" : undefined} />}
            {f.type === "number" && <Input type="number" value={val || ""} disabled={dis} onChange={(e) => set(f.id, e.target.value)} placeholder={f.placeholder} />}
            {f.type === "date" && (() => { const di = asDateInput(val); return (dis && !di && val !== null && val !== undefined && val !== "")
              ? <Input value={String(val)} disabled />
              : <Input type="date" value={di} disabled={dis} onChange={(e) => set(f.id, e.target.value)} />; })()}
            {f.type === "textarea" && <textarea value={val || ""} disabled={dis} onChange={(e) => set(f.id, e.target.value)} placeholder={f.placeholder} rows={3} className="w-full rounded-lg border border-slate-200 bg-slate-50 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 disabled:opacity-70" />}
            {f.type === "dropdown" && <div className="relative"><select value={val || ""} disabled={dis} onChange={(e) => set(f.id, e.target.value)} className={selCls}><option value="">Please select</option>{(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}</select><Chevron /></div>}
            {f.type === "radio" && <div className="space-y-1.5">{(f.options || []).map((o) => <label key={o} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer"><input type="radio" name={f.id} disabled={dis} checked={val === o} onChange={() => set(f.id, o)} className="accent-indigo-600" /> {o}</label>)}</div>}
            {f.type === "toggle" && (() => { const ts = toggleState(val); return f.allow_na || f.required ? (
              <div>
                <YesNoNa field={f} value={val} state={ts} disabled={dis} onChange={(v) => set(f.id, v)} />
                {f.required && ts === "No" && !f.no_is_answer && <p className="mt-1 text-xs font-semibold text-rose-600" data-testid={`fill-missing-${f.id}`}>Missing — please add details in the comments</p>}
              </div>
            ) : (
              <div className="flex items-center gap-3"><Switch checked={ts === "Yes"} disabled={dis} onCheckedChange={(v) => set(f.id, v ? "Yes" : "No")} data-testid={`fill-toggle-${f.id}`} /><span className={`text-sm font-medium ${ts === "Yes" ? "text-emerald-600" : ts === "No" ? "text-rose-600" : "text-slate-400"}`}>{ts || "Not set"}</span></div>
            ); })()}
            {f.type === "multiselect" && <div className="space-y-1.5">{(f.options || []).map((o) => <label key={o} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer"><input type="checkbox" disabled={dis} checked={(val || []).includes(o)} onChange={() => toggleMulti(f.id, o)} className="accent-indigo-600" /> {o}</label>)}</div>}
            {f.type === "checkbox" && <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer"><input type="checkbox" disabled={dis} checked={!!val} onChange={(e) => set(f.id, e.target.checked)} className="accent-indigo-600" />{f.label || "Checkbox"} {f.required && <span className="text-rose-500">*</span>}</label>}
            {f.type === "signature" && <SignaturePad value={val} disabled={dis} onChange={(v) => set(f.id, v)} />}
            {(f.type === "file" || f.type === "image" || f.type === "video") && (dis
              ? <FileAnswerView fieldId={f.id} val={val} attachmentRefs={submission?.attachment_refs} />
              : <div>
                  <input type="file" disabled={dis} accept={f.accept === "media" ? "image/*,video/*" : undefined} onChange={(e) => uploadFile(f.id, e.target.files?.[0])} className="block w-full text-sm text-slate-500 file:mr-3 file:rounded-lg file:border-0 file:bg-indigo-50 file:px-3 file:py-1.5 file:text-indigo-700" data-testid={`file-input-${f.id}`} />
                  {val?.uploading && <span className="mt-1 block text-xs text-slate-400">Uploading &amp; scanning…</span>}
                  {val?.attachment_id && <span className="mt-1 block text-xs text-emerald-600">Attached: {val.filename} ({context.task_legacy_id ? "added to Job Files" : "saved with this inspection"})</span>}
                </div>)}
            {f.help && <p className="text-xs text-slate-400 mt-1">{f.help}</p>}
          </div>
        );
      })}
      {fields.length === 0 && <div className="text-center text-sm text-slate-400 py-6">This form has no fields.</div>}
      {viewing && <ActionsApplied items={submission?.actions_applied} />}
    </>
  );

  const Meta = ({ label, value }) => (
    <div className="flex items-center justify-between gap-3 py-2.5 border-b border-slate-100">
      <span className="text-sm text-slate-500">{label}</span>
      <span className="text-sm font-medium text-slate-800 text-right">{value}</span>
    </div>
  );
  const Person = ({ name }) => (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs"><span className="h-1.5 w-1.5 rounded-full bg-rose-400" />{name || "—"}</span>
  );

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/40 flex justify-end" onClick={onClose} data-testid="form-filler">
      <div className={`bg-white h-full w-full sm:w-1/2 sm:max-w-none shadow-2xl overflow-hidden animate-in slide-in-from-right duration-300 flex flex-col`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-2 px-6 py-4 border-b border-slate-100">
          <div className="min-w-0 flex items-center gap-2">
            <h3 className="text-xl font-bold text-slate-900 truncate">{form ? form.name : (viewing ? "Submission" : "Fill Form")}</h3>
            {viewing && <span className="rounded-full bg-indigo-50 text-indigo-600 px-2 py-0.5 text-xs font-medium">Version 1</span>}
            {previewing && <span className="rounded-full bg-amber-50 text-amber-700 px-2 py-0.5 text-xs font-medium" data-testid="form-preview-badge">Preview · completed on-site</span>}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {viewing && <>
              <span className="hidden sm:inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-2.5 py-1 text-xs">Customer Visible</span>
              <span className="hidden sm:inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 px-2.5 py-1 text-xs">Client Visible</span>
              <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="submission-print"><Printer className="w-3.5 h-3.5 mr-1" />Print</Button>
              {locked ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-2.5 py-1 text-xs font-medium" data-testid="submission-locked">Signed · locked</span>
                : <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="submission-edit"><Pencil className="w-3.5 h-3.5 mr-1" />Edit</Button>}
            </>}
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700 ml-1" data-testid="form-filler-close"><X className="w-5 h-5" /></button>
          </div>
        </div>

        {!form ? <div className="flex-1 flex items-center justify-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div> : viewing ? (
          <div className="flex-1 flex flex-col sm:flex-row overflow-hidden" data-testid="submission-detail">
            <div className="flex-1 overflow-y-auto p-6 space-y-4">{renderFields()}</div>
            <aside className="w-full sm:w-80 shrink-0 border-t sm:border-t-0 sm:border-l border-slate-100 bg-slate-50/40 overflow-y-auto p-6">
              <Meta label="ID" value={`#${idNum(submission.id)}`} />
              <Meta label="Created By" value={<Person name={submission.submitted_by} />} />
              <Meta label="Created At" value={fmtDT(submission.submitted_at)} />
              <Meta label="Completed By" value={<Person name={submission.submitted_by} />} />
              <Meta label="Started At" value={fmtDT(submission.submitted_at)} />
              <Meta label="Completed At" value={fmtDT(submission.submitted_at)} />
              <Meta label="Completed On" value="App" />
              <Meta label="Device" value="SM-S928B" />
              <Meta label="Location Accuracy" value="28 Metres" />
            </aside>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-6 space-y-4">{renderFields()}</div>
        )}

        {previewing && form && (
          <div className="bg-white border-t border-slate-100 px-6 py-4 flex justify-between items-center gap-2">
            <span className="text-xs text-slate-400">Read-only preview of the on-site form.</span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>Close</Button>
              {canFill && <Button className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5" onClick={() => setEditing(true)} data-testid="form-preview-fill"><Pencil className="w-4 h-4" /> Fill this form</Button>}
            </div>
          </div>
        )}
        {!viewing && !previewing && form && (
          <div className="bg-white border-t border-slate-100 px-6 py-4 flex justify-end gap-2">
            <Button variant="outline" onClick={editing ? () => setEditing(false) : onClose}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5" disabled={busy} onClick={submit} data-testid="form-filler-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Submit Form</Button>
          </div>
        )}
      </div>
    </div>
  );
}
